import { MaterialGroup, SheetStackingType } from '@cutr/constants/cutlist';
import { EdgeProfileType } from '@cutr/constants/edge-profiling.ts';
import { SheetEdgeTrimType } from '@cutr/nesting';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { setGroupAndParts } from '@/api/materialsGroup';
import { useActions, useCutlistState } from '@/api/store';
import { useCurrentFeatures } from '@/theme';
import { getTrimConfigByType } from '@/utils/features';

import { EdgeProfileDropdown } from '../PartTable/EdgeProfileDropdown';
import { SheetEdgeTrimDropdown } from '../PartTableCommon/SheetEdgeTrimDropdown';
import { SheetStackingDropdown } from '../PartTableCommon/SheetStackingDropdown';
import { useProcessingSettingsConfig } from '../PartTableCommon/useProcessingSettingsConfig';
import styles from './MaterialGroupHeader.module.css';

export const MaterialGroupOptions = ({ group }: { group: MaterialGroup }) => {
  const { t } = useTranslation();
  const { supportedSheetEdgeTrims } = useCurrentFeatures();

  const { setPart } = useActions();
  const groupParts = useCutlistState(
    useShallow((state) => state.parts.filter((p) => p.groupId === group.id))
  );

  const {
    showEdgeTrimComponent,
    showSheetStackingComponent,
    showEdgeProfiling,
  } = useProcessingSettingsConfig({ group });

  const onSheetEdgeTrimTypeUpdate = (sheetEdgeTrimType: SheetEdgeTrimType) => {
    const sheetEdgeTrimConfig = getTrimConfigByType(
      supportedSheetEdgeTrims,
      sheetEdgeTrimType
    );

    const updatedGroup = {
      ...group,
      sheetEdgeTrimConfig,
    };

    setGroupAndParts(updatedGroup);
  };

  const onSheetStackingUpdate = (sheetStackingType: SheetStackingType) => {
    const updatedGroup = {
      ...group,
      sheetStackingType,
    };

    setGroupAndParts(updatedGroup);
  };

  const onEdgeProfileTypeUpdate = (edgeProfileType: EdgeProfileType) => {
    setGroupAndParts({ ...group, edgeProfile: edgeProfileType });

    groupParts.forEach((part) => {
      if (!part.edgeProfile) return;
      const { length1, length2, width1, width2 } = part.edgeProfile;
      if ([length1, length2, width1, width2].every((s) => s === 'none')) return;
      setPart({
        ...part,
        edgeProfile: {
          length1: length1 !== 'none' ? edgeProfileType : 'none',
          length2: length2 !== 'none' ? edgeProfileType : 'none',
          width1: width1 !== 'none' ? edgeProfileType : 'none',
          width2: width2 !== 'none' ? edgeProfileType : 'none',
        },
      });
    });
  };

  return (
    <div className={styles.optionsGroup}>
      <div className={styles.optionsBodyGrid}>
        {showSheetStackingComponent && (
          <div>
            <h4>{t('cutlist-form.sheetStackingTitle')}</h4>
            <SheetStackingDropdown
              group={group}
              onSheetStackingUpdate={onSheetStackingUpdate}
            />
          </div>
        )}
        {showEdgeProfiling && (
          <div>
            <h4>{t('cutlist-form.edgeProfile')}</h4>
            <EdgeProfileDropdown
              group={group}
              onEdgeProfileTypeUpdate={onEdgeProfileTypeUpdate}
              value={group.edgeProfile}
            />
          </div>
        )}
        {showEdgeTrimComponent && (
          <div>
            <h4>{t('cutlist-form.sheetEdges')}</h4>
            <SheetEdgeTrimDropdown
              group={group}
              onSheetEdgeTrimTypeUpdate={onSheetEdgeTrimTypeUpdate}
            />
          </div>
        )}
      </div>
    </div>
  );
};
